// src/components/settings/DriverAccessPanel.js
//
// Shows which drivers a user can see through their company assignments, and
// lets a Super Admin hand them a driver from a company they do NOT manage,
// with an optional expiry.
//
// Granting a driver also reveals that driver's loads — including work done for
// the company the driver now belongs to. That is deliberate: it is the escape
// hatch for "this admin still needs to follow this driver after a transfer".

import React, { useState } from 'react';
import {
  isVisibleToCompanies,
  limitToLinkedDrivers,
  getDriverGrants,
  getGrantedDriverIds,
  isGrantActive
} from '../../utils/companyScope';

// How long a manual grant lasts. null = never expires.
export const DRIVER_GRANT_DURATIONS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: 'No expiry', days: null },
];

const formatGrantExpiry = (expiresAt) => {
  if (!expiresAt) return 'No expiry';
  const d = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
  if (isNaN(d.getTime())) return 'No expiry';
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return 'Expired ' + d.toLocaleDateString();
  if (days === 0) return 'Expires today';
  return days + (days !== 1 ? ' days' : ' day') + ' left (' + d.toLocaleDateString() + ')';
};

const DriverAccessPanel = ({ user, drivers, canManage, onGrant, onRevoke, busyDriverId }) => {
  const [pendingDriverId, setPendingDriverId] = useState('');
  const [pendingDays, setPendingDays] = useState('30');

  const companyIds = user.assignedParentCompanyIds || [];
  const hasCompanyScope = companyIds.length > 0;

  // Drivers this user can actually see right now.
  //
  // For a dispatcher that is not "everyone in the company" — their team box
  // decides it. Listing the whole company here told a Super Admin the user
  // could see drivers the app would never show them, which is worse than
  // showing nothing: it is confidently wrong.
  const currentlyInCompany = hasCompanyScope
    ? limitToLinkedDrivers(
        drivers.filter(d => companyIds.includes(d.parentCompanyId)),
        user
      )
    : [];
  const currentlyInCompanyIds = new Set(currentlyInCompany.map(d => d.id));

  // Drivers who have since moved on but whose history still points here. The
  // user can see them, but only loads already tagged to their own company — a
  // grant is what opens up the rest.
  const viaHistoryOnly = hasCompanyScope
    ? drivers.filter(d => !currentlyInCompanyIds.has(d.id) && isVisibleToCompanies(d, companyIds))
    : [];

  const grants = getDriverGrants(user);
  const activeGrantIds = new Set(getGrantedDriverIds(user));

  // Anyone not already in this user's companies and not already granted —
  // which includes past drivers, since a grant upgrades what they can see.
  const grantable = drivers
    .filter(d => !currentlyInCompanyIds.has(d.id) && !activeGrantIds.has(d.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleAdd = () => {
    if (!pendingDriverId) return;
    const chosen = DRIVER_GRANT_DURATIONS.find(x => String(x.days) === pendingDays);
    onGrant(user, pendingDriverId, chosen ? chosen.days : 30);
    setPendingDriverId('');
  };

  return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-4">
      {/* Access that comes from company assignment */}
      <div>
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          Drivers through company access
        </p>
        {!hasCompanyScope ? (
          <p className="text-sm text-gray-500 mt-1">
            Not assigned to any company, so this user sees every driver in the tenant.
          </p>
        ) : currentlyInCompany.length === 0 ? (
          <p className="text-sm text-gray-500 mt-1">
            None &mdash; no drivers currently belong to this user&apos;s companies.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {currentlyInCompany.map(d => (
              <span
                key={d.id}
                className="px-2 py-1 text-xs rounded-full bg-white border border-gray-200 text-gray-700"
              >
                {d.name || d.id}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Moved on, but past work is still visible */}
      {viaHistoryOnly.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Previously in these companies
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Visible, but only for loads they ran under this user&apos;s companies. Grant one below
            to also show their work at the company they moved to.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {viaHistoryOnly.map(d => (
              <span
                key={d.id}
                className="px-2 py-1 text-xs rounded-full bg-amber-50 border border-amber-200 text-amber-800"
              >
                {d.name || d.id}
                {d.parentCompanyName ? ' → ' + d.parentCompanyName : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Manual grants */}
      <div>
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          Manually granted drivers
        </p>
        {grants.length === 0 ? (
          <p className="text-sm text-gray-500 mt-1">None.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {grants.map(g => {
              const active = isGrantActive(g);
              return (
                <li
                  key={g.driverId}
                  className={
                    'flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-sm ' +
                    (active
                      ? 'bg-white border-gray-200'
                      : 'bg-gray-100 border-gray-200 text-gray-400')
                  }
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{g.driverName || g.driverId}</span>
                    <span className={active ? 'text-xs text-gray-500' : 'text-xs text-gray-400'}>
                      {formatGrantExpiry(g.expiresAt)}
                    </span>
                    {!active && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                        Expired
                      </span>
                    )}
                  </span>
                  {canManage && (
                    <button
                      onClick={() => onRevoke(user, g.driverId)}
                      disabled={busyDriverId === g.driverId}
                      className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-40 flex-shrink-0"
                    >
                      {busyDriverId === g.driverId ? 'Removing...' : 'Remove'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Grant a new driver */}
      {canManage && (
        <div className="pt-3 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Give access to another company&apos;s driver
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={pendingDriverId}
              onChange={(e) => setPendingDriverId(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a driver...</option>
              {grantable.map(d => (
                <option key={d.id} value={d.id}>
                  {(d.name || d.id) + (d.parentCompanyName ? ' — ' + d.parentCompanyName : '')}
                </option>
              ))}
            </select>
            <select
              value={pendingDays}
              onChange={(e) => setPendingDays(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm sm:w-40"
            >
              {DRIVER_GRANT_DURATIONS.map(d => (
                <option key={d.label} value={String(d.days)}>{d.label}</option>
              ))}
            </select>
            <button
              onClick={handleAdd}
              disabled={!pendingDriverId || busyDriverId === pendingDriverId}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap"
            >
              Give access
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            This user will see the driver and <span className="font-medium">all of that
            driver&apos;s loads</span>, including work done for the company the driver
            belongs to now.
          </p>
        </div>
      )}
    </div>
  );
};

export default DriverAccessPanel;
